import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Button, Spinner } from "reactstrap";

import { useFetchCatalogProductWorkspaceQuery } from "../../api/api";
import { buildProductColors } from "../../utils/catalogColorCover";
import {
  buildEnrichmentPayload,
  coerceNullableNumber,
  createEmptyDetailSection,
  createEmptyEnrichmentForm,
  createEmptyMediaItem,
  getProductFamilyOptions,
  getReferenceOptionLabel,
} from "../../utils/catalogProductStudioShared";
import {
  buildInventoryPayload,
  buildMediaPayload,
  buildPublishChecklist,
  buildSimpleOptionDimensions,
  buildSimpleVariantRows,
  buildSizeRangeSelection,
  buildStockByColor,
  buildStructurePayload,
  buildVariantOverridesPayload,
  countFilledWebshopFields,
  findColorSuggestion,
  getRowSizeValue,
  HARD_VARIANT_LIMIT,
  groupVariantRowsByColor,
  hydrateWizardStateFromWorkspace,
  inferApparelSizeSystem,
  inferOptionGuidance,
  moveMediaItemWithinGroup,
  normalizeColorLabel,
  normalizeLookupKey,
  normalizeSimpleOptionValues,
  normalizeSizeLabel,
  orderSizes,
  resolveColorSwatchBackground,
  resolveMediaColorCovers,
  resolveSizePresetKey,
  DEFAULT_SIZE_SELECTION,
  SIZE_PRESETS,
  stageFailureTranslationKey,
  WIZARD_STAGES,
} from "./wizardCreateHelpers";
import Step1Product from "./steps/Step1Product";
import Step2Variants from "./steps/Step2Variants";
import Step3PhotosStock from "./steps/Step3PhotosStock";
import Step4Review from "./steps/Step4Review";
import CreatePreviewPanel from "./steps/CreatePreviewPanel";
import WizardIcon from "./steps/WizardIcon";

const emptyCore = () => ({
  title: "",
  description: "",
  price: "",
  compareAtPrice: "",
  cost: "",
  brandId: "",
  productFamily: "shoes",
  merchandisingTypeId: "",
  slug: "",
  baseSku: "",
  metaCatalogEnabled: false,
  enrichment: createEmptyEnrichmentForm(),
});

const CREATE_ANOTHER_STORAGE_KEY = "catalogWizard.createAnother";

const readCreateAnotherPreference = () => {
  try {
    return window.localStorage.getItem(CREATE_ANOTHER_STORAGE_KEY) !== "false";
  } catch (_error) {
    return true;
  }
};

/**
 * The next product of a batch usually shares its brand, family, type and
 * sizes with the last one; everything that identifies or prices a single
 * product starts empty again.
 */
export const buildNextProductCore = (previous) => ({
  ...emptyCore(),
  brandId: previous.brandId,
  productFamily: previous.productFamily,
  merchandisingTypeId: previous.merchandisingTypeId,
  metaCatalogEnabled: previous.metaCatalogEnabled,
});

const isProductCodeTakenError = (error) =>
  error?.status === 409 &&
  error?.data?.details?.code === "PRODUCT_CODE_TAKEN" &&
  Boolean(error.data.details.conflict);

const CatalogProductCreateWizard = ({
  t,
  brands,
  colors: cmsColors,
  merchandisingTypes,
  locations,
  onProductFamilyChange,
  createCatalogProductDraft,
  checkCatalogProductCode,
  uploadCatalogMediaAsset,
  updateCatalogProductCore,
  saveCatalogProductStructure,
  replaceCatalogProductMedia,
  isUploadingMediaAsset,
  updateCatalogProductInventory,
  updateCatalogVariants,
  updateCatalogProductPublishing,
  navigate,
  showSuccess,
  showError,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  // Only a draftId present when the page first loads (a reload / shared link)
  // is a resume. The draftId this wizard writes itself after creating the
  // draft must never trigger a re-hydration, or it would overwrite the
  // merchant's unsaved local state with the partially-saved server copy.
  const [resumedProductId] = useState(() => {
    const draftIdParam = searchParams.get("draftId");
    return draftIdParam && /^\d+$/.test(draftIdParam) ? Number(draftIdParam) : null;
  });

  const [step, setStep] = useState(1);
  const [productId, setProductId] = useState(resumedProductId);
  const [isHydrated, setIsHydrated] = useState(!resumedProductId);
  const hasAppliedHydrationRef = useRef(false);

  const [core, setCore] = useState(emptyCore());
  const [publishStatus, setPublishStatus] = useState("draft");
  const [colors, setColors] = useState([]);
  const [sizes, setSizes] = useState([]);
  // Until the merchant picks sizes, the selection follows the preset's default range.
  const sizesTouchedRef = useRef(false);
  const [apparelSizeSystem, setApparelSizeSystem] = useState("letters");
  const [colorSearchTerm, setColorSearchTerm] = useState("");
  const [mediaItems, setMediaItems] = useState([]);
  const [quantityByRowKey, setQuantityByRowKey] = useState({});
  const [locationId, setLocationId] = useState("");
  const [stageError, setStageError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  // The live product already using the typed code (the title), if any.
  const [codeConflict, setCodeConflict] = useState(null);
  // "Krijo një tjetër": stay in the wizard after a create. On by default.
  const [createAnother, setCreateAnother] = useState(readCreateAnotherPreference);
  // Products created in this batch, newest first, so their labels are not forgotten.
  const [createdInBatch, setCreatedInBatch] = useState([]);

  const { data: resumedWorkspace, isError: isResumeError, isFetching: isFetchingResume } =
    useFetchCatalogProductWorkspaceQuery(resumedProductId, {
      skip: !resumedProductId,
    });

  useEffect(() => {
    if (!resumedProductId || hasAppliedHydrationRef.current || isFetchingResume) {
      return;
    }

    // A stale/invalid draftId (deleted product, wrong company, network error)
    // must never leave the wizard stuck on a spinner — fall back to a fresh
    // blank product instead, and drop the dead query param.
    if (isResumeError || !resumedWorkspace) {
      hasAppliedHydrationRef.current = true;
      setIsHydrated(true);
      setProductId(null);
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete("draftId");
      setSearchParams(nextParams, { replace: true });
      return;
    }

    const hydrated = hydrateWizardStateFromWorkspace(resumedWorkspace);
    if (!hydrated) {
      return;
    }

    hasAppliedHydrationRef.current = true;
    setCore(hydrated.core);
    setPublishStatus(hydrated.publishStatus);
    setColors(hydrated.colors);
    setSizes(hydrated.sizes);
    sizesTouchedRef.current = true;
    setApparelSizeSystem(inferApparelSizeSystem(hydrated.sizes));
    setMediaItems(hydrated.mediaItems);
    setQuantityByRowKey(hydrated.quantityByRowKey);
    setIsHydrated(true);
    showSuccess(t("catalogProductStudio.wizardResumedNotice"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resumedProductId, resumedWorkspace, isResumeError, isFetchingResume]);

  useEffect(() => {
    onProductFamilyChange?.(core.productFamily);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [core.productFamily]);

  useEffect(() => {
    if (!locationId && locations.length) {
      setLocationId(String(locations[0].id));
    }
  }, [locationId, locations]);

  const untitledLabel = t("catalogProductStudio.untitled");
  const productFamilyOptions = useMemo(() => getProductFamilyOptions(t), [t]);

  const selectedMerchandisingType = useMemo(
    () =>
      merchandisingTypes.find(
        (type) => String(type.id) === String(core.merchandisingTypeId || "")
      ) || null,
    [merchandisingTypes, core.merchandisingTypeId]
  );

  const optionGuidance = useMemo(
    () =>
      inferOptionGuidance(t, {
        productFamily: core.productFamily,
        merchandisingTypeLabel: getReferenceOptionLabel(selectedMerchandisingType, untitledLabel),
      }),
    [t, core.productFamily, selectedMerchandisingType, untitledLabel]
  );

  const sizePresetKey = resolveSizePresetKey(optionGuidance.sizePresetKey, apparelSizeSystem);
  const recommendedSizes = useMemo(
    () => (sizePresetKey ? SIZE_PRESETS[sizePresetKey] || [] : []),
    [sizePresetKey]
  );

  useEffect(() => {
    if (!isHydrated || sizesTouchedRef.current) return;
    setSizes(DEFAULT_SIZE_SELECTION[sizePresetKey] || []);
  }, [isHydrated, sizePresetKey]);

  const cmsColorOptions = useMemo(
    () =>
      (cmsColors || [])
        .map((colorRecord) => {
          const label = normalizeColorLabel(getReferenceOptionLabel(colorRecord, untitledLabel));
          if (!label) return null;
          return {
            id: colorRecord.id,
            label,
            colorRecord,
          };
        })
        .filter(Boolean)
        .map((entry) => ({
          ...entry,
          swatch: resolveColorSwatchBackground(entry.label, entry.colorRecord),
        })),
    [cmsColors, untitledLabel]
  );
  const colorDictionary = useMemo(() => cmsColorOptions.map((option) => option.label), [cmsColorOptions]);
  // Swatches must come from the CMS colour record (hexCode) — Albanian names
  // like "Zi" or "Taba" are not CSS colours, so a label-only lookup would
  // paint them with a hashed fallback instead of their real colour.
  const swatchByLabel = useMemo(
    () => new Map(cmsColorOptions.map((option) => [normalizeLookupKey(option.label), option.swatch])),
    [cmsColorOptions]
  );
  const getSwatch = useCallback(
    (label) => swatchByLabel.get(normalizeLookupKey(label)) || resolveColorSwatchBackground(label),
    [swatchByLabel]
  );

  const hasOptions = colors.length > 0 || sizes.length > 0;
  const optionDimensions = useMemo(
    () => buildSimpleOptionDimensions({ hasOptions, sizes, colors, t }),
    [hasOptions, sizes, colors, t]
  );
  const generatedVariantRows = useMemo(
    () => buildSimpleVariantRows({ title: core.title, hasOptions, sizes, colors, t }),
    [core.title, hasOptions, sizes, colors, t]
  );
  const variantCount = generatedVariantRows.length;
  const isOverVariantLimit = variantCount > HARD_VARIANT_LIMIT;
  const colorGroups = useMemo(() => groupVariantRowsByColor(generatedVariantRows), [generatedVariantRows]);
  const totalStock = useMemo(
    () =>
      generatedVariantRows.reduce(
        (sum, row) => sum + Math.max(0, Number(quantityByRowKey[row.key] || 0) || 0),
        0
      ),
    [generatedVariantRows, quantityByRowKey]
  );
  const mediaCount = useMemo(
    () => mediaItems.filter((item) => item.storageKey || item.sourceUrl).length,
    [mediaItems]
  );
  const coverImageUrl = useMemo(() => {
    const cover = mediaItems.find((item) => item.isCover && item.sourceUrl);
    return cover?.sourceUrl || mediaItems.find((item) => item.sourceUrl)?.sourceUrl || null;
  }, [mediaItems]);

  const checklist = useMemo(
    () =>
      buildPublishChecklist({
        title: core.title,
        price: core.price,
        variantCount,
        totalStock,
        mediaCount,
        colors,
        mediaItems,
        enrichment: core.enrichment,
        isOverVariantLimit,
      }),
    [
      core.title,
      core.price,
      core.enrichment,
      variantCount,
      totalStock,
      mediaCount,
      colors,
      mediaItems,
      isOverVariantLimit,
    ]
  );

  const brandName = useMemo(() => {
    const brand = brands.find((entry) => String(entry.id) === String(core.brandId));
    return brand ? getReferenceOptionLabel(brand, "") : "";
  }, [brands, core.brandId]);
  const categoryLabel = useMemo(
    () => productFamilyOptions.find((option) => option.value === core.productFamily)?.label || "",
    [productFamilyOptions, core.productFamily]
  );
  const typeLabel = selectedMerchandisingType
    ? getReferenceOptionLabel(selectedMerchandisingType, "")
    : "";
  const webshopFilledCount = countFilledWebshopFields(core.enrichment);

  // ─── Step 1 handlers ──────────────────────────────────────────────────
  const handleCoreFieldChange = (name, value) => {
    setCore((current) => {
      const next = { ...current, [name]: value };
      // The compare-at price follows the price until it is edited to differ.
      if (name === "price" && (current.compareAtPrice === "" || current.compareAtPrice === current.price)) {
        next.compareAtPrice = value;
      }
      return next;
    });
    setFieldErrors((current) => ({ ...current, [name]: undefined }));
    if (name === "title") setCodeConflict(null);
  };
  const handleEnrichmentChange = (name, value) => {
    setCore((current) => ({ ...current, enrichment: { ...current.enrichment, [name]: value } }));
  };
  const handleAddDetailSection = () => {
    setCore((current) => ({
      ...current,
      enrichment: {
        ...current.enrichment,
        detailSections: [...(current.enrichment.detailSections || []), createEmptyDetailSection()],
      },
    }));
  };
  const handleRemoveDetailSection = (index) => {
    setCore((current) => ({
      ...current,
      enrichment: {
        ...current.enrichment,
        detailSections: (current.enrichment.detailSections || []).filter((_section, i) => i !== index),
      },
    }));
  };
  const handleDetailSectionChange = (index, field, value) => {
    setCore((current) => ({
      ...current,
      enrichment: {
        ...current.enrichment,
        detailSections: (current.enrichment.detailSections || []).map((section, i) =>
          i === index ? { ...section, [field]: value } : section
        ),
      },
    }));
  };

  // ─── Step 2 handlers ──────────────────────────────────────────────────
  const toggleColor = (label) => {
    const normalized = normalizeColorLabel(label);
    setColors((current) =>
      current.includes(normalized)
        ? current.filter((entry) => entry !== normalized)
        : normalizeSimpleOptionValues([...current, normalized]).sort()
    );
  };
  // Removing a colour also drops the photos uploaded under it, so no image is
  // left scoped to a colour that will not exist after the structure save.
  const removeColor = (label) => {
    const photoCount = mediaItems.filter((item) => item.colorLabel === label).length;
    if (
      photoCount > 0 &&
      !window.confirm(t("catalogProductStudio.step3RemoveColorConfirm").replace("{color}", label))
    ) {
      return;
    }
    setColors((current) => current.filter((entry) => entry !== label));
    setMediaItems((current) => {
      const next = current.filter((item) => item.colorLabel !== label);
      if (!next.some((item) => item.isCover) && next[0]) {
        next[0] = { ...next[0], isCover: true };
      }
      return next;
    });
  };
  const addCustomColor = (value) => {
    const normalized = findColorSuggestion(value, colorDictionary) || normalizeColorLabel(value);
    if (!normalized) return;
    setColors((current) => normalizeSimpleOptionValues([...current, normalized]).sort());
  };
  const toggleSize = (value) => {
    sizesTouchedRef.current = true;
    setSizes((current) =>
      orderSizes(
        current.includes(value)
          ? current.filter((entry) => entry !== value)
          : normalizeSimpleOptionValues([...current, value]),
        recommendedSizes
      )
    );
  };
  const addCustomSize = (value) => {
    const normalized = normalizeSizeLabel(value);
    if (!normalized) return;
    sizesTouchedRef.current = true;
    setSizes((current) => orderSizes(normalizeSimpleOptionValues([...current, normalized]), recommendedSizes));
  };
  // Switching letters ↔ numbers drops the old system's preset sizes but keeps
  // any custom sizes the merchant typed in.
  const changeApparelSizeSystem = (nextSystem) => {
    if (nextSystem === apparelSizeSystem) return;
    const nextPreset = SIZE_PRESETS[resolveSizePresetKey("apparel", nextSystem)] || [];
    setApparelSizeSystem(nextSystem);
    setSizes((current) =>
      orderSizes(
        current.filter((size) => !recommendedSizes.includes(size)),
        nextPreset
      )
    );
  };
  // "Zgjidh shpejt" replaces the preset selection with the range but keeps
  // any custom sizes the merchant typed in.
  const applySizeRange = (from, to) => {
    const range = buildSizeRangeSelection(recommendedSizes, from, to);
    if (!range.length) return;
    sizesTouchedRef.current = true;
    setSizes((current) =>
      orderSizes(
        normalizeSimpleOptionValues([
          ...range,
          ...current.filter((size) => !recommendedSizes.includes(size)),
        ]),
        recommendedSizes
      )
    );
  };

  // ─── Step 3 handlers ──────────────────────────────────────────────────
  const handleUploadForColor = async (colorLabel, files) => {
    const selectedFiles = Array.from(files || []);
    if (!selectedFiles.length) return;

    try {
      const uploaded = [];
      for (const file of selectedFiles) {
        const formData = new FormData();
        formData.append("file", file);
        const response = await uploadCatalogMediaAsset(formData).unwrap();
        uploaded.push({
          ...createEmptyMediaItem(),
          storageKey: response?.storageKey || "",
          sourceUrl: response?.sourceUrl || "",
          originalName: response?.originalName || file.name,
          thumbnailKey: response?.thumbnailKey || "",
          cardKey: response?.cardKey || "",
          fullKey: response?.fullKey || "",
          colorLabel: colorLabel || null,
        });
      }

      setMediaItems((current) => {
        const merged = [...current, ...uploaded];
        if (!merged.some((item) => item.isCover) && merged[0]) {
          merged[0] = { ...merged[0], isCover: true };
        }
        // Auto color-cover default: first photo in a fresh color group becomes
        // that color's cover, mirroring the product-cover auto-promotion.
        if (colorLabel && !merged.some((item) => item.colorLabel === colorLabel && item.isColorCover)) {
          const firstIndex = merged.findIndex((item) => item.colorLabel === colorLabel);
          if (firstIndex !== -1) {
            merged[firstIndex] = { ...merged[firstIndex], isColorCover: true };
          }
        }
        return merged;
      });
    } catch (_error) {
      showError(t("catalogProductStudio.errorUploadImage"));
    }
  };
  const removeMediaItem = (id) => {
    setMediaItems((current) => {
      const next = current.filter((item) => item.id !== id);
      if (!next.some((item) => item.isCover) && next[0]) {
        next[0] = { ...next[0], isCover: true };
      }
      return next;
    });
  };
  const setProductCover = (id) => {
    setMediaItems((current) => current.map((item) => ({ ...item, isCover: item.id === id })));
  };
  const setColorCover = (id, colorLabel) => {
    setMediaItems((current) =>
      current.map((item) => {
        if (item.colorLabel !== colorLabel) return item;
        return { ...item, isColorCover: item.id === id };
      })
    );
  };
  const moveMediaItem = (id, direction) => {
    setMediaItems((current) => moveMediaItemWithinGroup(current, id, direction));
  };
  // "Vendos si ngjyrë kryesore": the colour's cover photo becomes the product
  // cover (isPrimary). The default variant is still resolved by the API.
  const setPrimaryColor = (colorLabel) => {
    setMediaItems((current) => {
      const groupItems = current.filter((item) => item.colorLabel === colorLabel);
      const target = groupItems.find((item) => item.isColorCover) || groupItems[0];
      if (!target) return current;
      return current.map((item) => ({ ...item, isCover: item.id === target.id }));
    });
  };
  const handleQuantityChange = (rowKey, value) => {
    setQuantityByRowKey((current) => ({ ...current, [rowKey]: value }));
  };
  const sameStockForColor = (rows, value) => {
    const quantity = Math.max(0, Number(value || 0) || 0);
    setQuantityByRowKey((current) => {
      const next = { ...current };
      rows.forEach((row) => {
        next[row.key] = String(quantity);
      });
      return next;
    });
  };
  const clearStockForColor = (rows) => {
    setQuantityByRowKey((current) => {
      const next = { ...current };
      rows.forEach((row) => {
        next[row.key] = "0";
      });
      return next;
    });
  };

  // ─── Step navigation ────────────────────────────────────────────────
  const validateStep1 = () => {
    const errors = {};
    if (!core.title.trim()) errors.title = t("catalogProductStudio.errorNameRequired");
    if (coerceNullableNumber(core.price) == null) errors.price = t("catalogProductStudio.errorPriceRequired");
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const flagCodeConflict = (conflict) => {
    setCodeConflict(conflict);
    setFieldErrors((current) => ({
      ...current,
      title: t("catalogProductStudio.errorCodeTaken").replace("{title}", conflict.title),
    }));
  };

  // Two live products may not share a code. The server enforces it too; this
  // only catches it before the merchant fills in the rest of the wizard.
  const ensureCodeAvailable = async () => {
    if (!checkCatalogProductCode) return true;
    try {
      const result = await checkCatalogProductCode({
        title: core.title.trim(),
        excludeProductId: productId || undefined,
      }).unwrap();
      if (result?.conflict) {
        flagCodeConflict(result.conflict);
        return false;
      }
    } catch (_error) {
      // An unreachable check never blocks; the create call is still guarded.
    }
    return true;
  };

  const goToStep = (nextStep) => {
    if (nextStep > step && step === 1) {
      if (!validateStep1()) return;
      if (checkCatalogProductCode) {
        ensureCodeAvailable().then((available) => available && setStep(nextStep));
        return;
      }
    }
    setStep(nextStep);
  };

  // ─── Submit sequence ────────────────────────────────────────────────
  // targetStatus is the publishing API value: "draft", "unpublished"
  // (active in the shop only) or "active" (also published online).
  const runSubmit = async (targetStatus) => {
    if (!validateStep1()) {
      setStep(1);
      showError(t("catalogProductStudio.errorNameRequired"));
      return;
    }
    if (!(await ensureCodeAvailable())) {
      setStep(1);
      return;
    }
    if (!locationId) {
      showError(t("catalogProductStudio.errorStockLocation"));
      setStep(3);
      return;
    }

    setIsSubmitting(true);
    setStageError(null);
    let pid = productId;
    let stage = WIZARD_STAGES.DRAFT;

    try {
      const productMode = optionDimensions.length ? "variant" : "simple";

      if (!pid) {
        const draft = await createCatalogProductDraft({
          title: core.title,
          description: core.description || undefined,
          productFamily: core.productFamily,
          productMode,
          enrichment: buildEnrichmentPayload(core.enrichment),
        }).unwrap();
        pid = draft.product.id;
        setProductId(pid);
        const nextParams = new URLSearchParams(searchParams);
        nextParams.set("draftId", String(pid));
        setSearchParams(nextParams, { replace: true });
      }

      stage = WIZARD_STAGES.CORE;
      await updateCatalogProductCore({
        productId: pid,
        body: {
          title: core.title,
          description: core.description || undefined,
          productFamily: core.productFamily,
          brandId: core.brandId ? Number(core.brandId) : undefined,
          merchandisingTypeId: core.merchandisingTypeId ? Number(core.merchandisingTypeId) : undefined,
          slug: core.slug || undefined,
          baseSku: core.baseSku || undefined,
          metaCatalogEnabled: core.metaCatalogEnabled,
          enrichment: buildEnrichmentPayload(core.enrichment),
        },
      }).unwrap();

      stage = WIZARD_STAGES.STRUCTURE;
      const structureResponse = await saveCatalogProductStructure({
        productId: pid,
        body: buildStructurePayload({
          productMode,
          optionDimensions,
          price: core.price,
          compareAtPrice: core.compareAtPrice,
          cost: core.cost,
        }),
      }).unwrap();
      const workspaceAfterStructure = structureResponse?.data || structureResponse;
      const persistedVariants = workspaceAfterStructure?.variants || [];
      const resolvedColors = buildProductColors(workspaceAfterStructure?.options || [], persistedVariants);

      stage = WIZARD_STAGES.VARIANTS;
      const variantOverrides = buildVariantOverridesPayload({ persistedVariants, rowOverrides: {} });
      if (variantOverrides.length) {
        await updateCatalogVariants({ productId: pid, body: { items: variantOverrides } }).unwrap();
      }

      stage = WIZARD_STAGES.MEDIA;
      const resolvedMediaItems = resolveMediaColorCovers(mediaItems, resolvedColors);
      const mediaPayload = buildMediaPayload(resolvedMediaItems);
      if (mediaPayload.length) {
        await replaceCatalogProductMedia({ productId: pid, body: { items: mediaPayload } }).unwrap();
      }

      stage = WIZARD_STAGES.INVENTORY;
      const inventoryResult = await updateCatalogProductInventory({
        productId: pid,
        body: buildInventoryPayload({ persistedVariants, locationId, quantityByRowKey }),
      }).unwrap();
      // Opening stock that needs labels: the product page offers "Print X labels"
      // (optional; nothing is offered when the opening stock is zero).
      const createdReceipt = inventoryResult?.receipt?.labelsOutstanding > 0 ? inventoryResult.receipt : null;

      if (targetStatus !== "draft") {
        stage = WIZARD_STAGES.PUBLISHING;
        await updateCatalogProductPublishing({ productId: pid, body: { status: targetStatus } }).unwrap();
      }

      showSuccess(
        targetStatus === "active"
          ? t("catalogProductStudio.successCreatedPublished")
          : targetStatus === "unpublished"
          ? t("catalogProductStudio.successCreatedPosOnly")
          : t("catalogProductStudio.successCreatedDraft")
      );
      if (createAnother) {
        setCreatedInBatch((current) => [{ id: pid, title: core.title.trim(), receipt: createdReceipt }, ...current]);
        startNextProduct({ keepShared: true });
        return;
      }
      if (createdReceipt) navigate(`/admin/catalog/products/${pid}`, { state: { createdReceipt } });
      else navigate(`/admin/catalog/products/${pid}`);
    } catch (error) {
      if (isProductCodeTakenError(error)) {
        flagCodeConflict(error.data.details.conflict);
        setStep(1);
        showError(t("catalogProductStudio.errorCodeTaken").replace("{title}", error.data.details.conflict.title));
        return;
      }
      // Only the translated stage message is shown — never raw backend text.
      setStageError({ stage });
      showError(t(stageFailureTranslationKey(stage)));
    } finally {
      setIsSubmitting(false);
    }
  };


  const changeCreateAnother = (checked) => {
    setCreateAnother(checked);
    try {
      window.localStorage.setItem(CREATE_ANOTHER_STORAGE_KEY, String(checked));
    } catch (_error) {
      // The choice simply is not remembered.
    }
  };

  // Back to step 1 for a new product. `keepShared` carries over what a batch
  // shares (brand, family, type, sizes, stock location); otherwise a blank start.
  const startNextProduct = ({ keepShared }) => {
    setCore((current) => (keepShared ? buildNextProductCore(current) : emptyCore()));
    if (!keepShared) {
      sizesTouchedRef.current = false;
      setApparelSizeSystem("letters");
      setSizes([]);
    }
    setProductId(null);
    setColors([]);
    setColorSearchTerm("");
    setMediaItems([]);
    setQuantityByRowKey({});
    setStageError(null);
    setFieldErrors({});
    setCodeConflict(null);
    setStep(1);
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete("draftId");
    setSearchParams(nextParams, { replace: true });
    window.scrollTo?.(0, 0);
  };

  const matrixText =
    colors.length && sizes.length
      ? t("catalogProductStudio.matrixBoth")
          .replace("{colors}", String(colors.length))
          .replace("{sizes}", String(sizes.length))
      : colors.length
      ? t("catalogProductStudio.matrixColorOnly").replace("{colors}", String(colors.length))
      : sizes.length
      ? t("catalogProductStudio.matrixSizeOnly").replace("{sizes}", String(sizes.length))
      : t("catalogProductStudio.matrixNone");
  const variantCountText =
    variantCount === 1
      ? t("catalogProductStudio.variantsCountOne")
      : t("catalogProductStudio.variantsCount").replace("{count}", String(variantCount));

  const stepConfig = [
    {
      id: 1,
      title: t("catalogProductStudio.wizardStepProduct"),
      hint: t("catalogProductStudio.wizardStepProductHint"),
    },
    {
      id: 2,
      title: t("catalogProductStudio.wizardStepVariants"),
      hint: t("catalogProductStudio.wizardStepVariantsHint"),
      doneHint: `${matrixText} (${variantCountText})`,
    },
    {
      id: 3,
      title: t("catalogProductStudio.wizardStepPhotosStock"),
      hint: t("catalogProductStudio.wizardStepPhotosStockHint"),
      doneHint: `${
        mediaCount
          ? t("catalogProductStudio.wizardPhotosUploaded").replace("{count}", String(mediaCount))
          : t("catalogProductStudio.wizardNoPhotos")
      } · ${
        totalStock ? t("catalogProductStudio.wizardStockSet") : t("catalogProductStudio.wizardStockEmpty")
      }`,
    },
    {
      id: 4,
      title: t("catalogProductStudio.wizardStepReview"),
      hint: t("catalogProductStudio.wizardStepReviewHint"),
    },
  ];

  // Cover first, then the rest in saved order — what the storefront shows.
  const orderedPhotoUrls = useMemo(() => {
    const withUrl = mediaItems.filter((item) => item.sourceUrl);
    const cover = withUrl.find((item) => item.isCover);
    return [
      ...(cover ? [cover.sourceUrl] : []),
      ...withUrl.filter((item) => item !== cover).map((item) => item.sourceUrl),
    ];
  }, [mediaItems]);
  const stockByColor = useMemo(
    () => buildStockByColor(colorGroups, quantityByRowKey),
    [colorGroups, quantityByRowKey]
  );
  const colorSummaries = useMemo(
    () =>
      colorGroups.map((group, index) => {
        const groupItems = mediaItems.filter((item) =>
          group.color ? item.colorLabel === group.color : !item.colorLabel
        );
        const cover = groupItems.find((item) => item.isColorCover) || groupItems[0];
        return {
          color: group.color,
          variantCount: group.rows.length,
          stock: stockByColor[index]?.total || 0,
          sizes: group.rows.map(getRowSizeValue).filter(Boolean),
          coverUrl: cover?.sourceUrl || null,
        };
      }),
    [colorGroups, mediaItems, stockByColor]
  );

  if (resumedProductId && !isHydrated) {
    return (
      <div className="content catalog-product-studio catalog-studio-loading">
        <Spinner color="primary" />
      </div>
    );
  }

  const nextDisabled = step === 2 && isOverVariantLimit;
  const submitDisabled = isSubmitting || checklist.hard.length > 0;

  return (
    <div className="content catalog-product-studio catalog-wizard-page">
      <button type="button" className="catalog-wizard-back" onClick={() => navigate("/admin/catalog/products")}>
        <WizardIcon name="arrowLeft" size={16} />
        {t("catalogProductStudio.wizardBackToProducts")}
      </button>
      <h1 className="catalog-wizard-page-title">{t("catalogProductStudio.wizardTitle")}</h1>
      <p className="catalog-wizard-page-subtitle">
        {step === 4
          ? t("catalogProductStudio.wizardReviewSubtitle")
          : t("catalogProductStudio.wizardSubtitle")}
      </p>

      <nav className="catalog-wizard-stepper" aria-label={t("catalogProductStudio.wizardStepsAria")}>
        {stepConfig.map((entry) => {
          const isDone = step > entry.id;
          const isActive = step === entry.id;
          return (
            <button
              key={entry.id}
              type="button"
              className={`catalog-wizard-stepper__step${isActive ? " is-active" : ""}${isDone ? " is-done" : ""}`}
              aria-current={isActive ? "step" : undefined}
              disabled={entry.id > step + 1 || isSubmitting}
              onClick={() => goToStep(entry.id)}
            >
              <span className="catalog-wizard-stepper__circle" aria-hidden="true">
                {isDone ? "✓" : entry.id}
              </span>
              <span className="catalog-wizard-stepper__text">
                <span className="catalog-wizard-stepper__title">{entry.title}</span>
                <span className="catalog-wizard-stepper__hint">
                  {isDone && entry.doneHint ? entry.doneHint : entry.hint}
                </span>
              </span>
            </button>
          );
        })}
      </nav>

      {createdInBatch.length ? (
        <div className="catalog-wizard-batch" role="status">
          <WizardIcon name="check" size={18} className="catalog-wizard-batch__icon" />
          <div className="catalog-wizard-batch__body">
            <strong>
              {t("catalogProductStudio.batchCreatedTitle").replace("{count}", String(createdInBatch.length))}
            </strong>
            <ul className="catalog-wizard-batch__list">
              {createdInBatch.map((entry) => (
                <li key={entry.id}>
                  <span className="catalog-wizard-batch__name">{entry.title}</span>
                  <button
                    type="button"
                    className="catalog-wizard-batch__link"
                    onClick={() =>
                      navigate(
                        `/admin/catalog/products/${entry.id}`,
                        entry.receipt ? { state: { createdReceipt: entry.receipt } } : undefined
                      )
                    }
                  >
                    {entry.receipt
                      ? t("catalogProductStudio.batchPrintLabels").replace(
                          "{count}",
                          String(entry.receipt.labelsOutstanding)
                        )
                      : t("catalogProductStudio.openExistingProduct")}
                  </button>
                </li>
              ))}
            </ul>
          </div>
          <button
            type="button"
            className="catalog-wizard-batch__reset"
            disabled={isSubmitting}
            onClick={() => startNextProduct({ keepShared: false })}
          >
            {t("catalogProductStudio.batchStartBlank")}
          </button>
        </div>
      ) : null}

      {stageError ? (
        <div className="catalog-wizard-stage-error" role="alert">
          <span>
            {t(stageFailureTranslationKey(stageError.stage))}
          </span>
          <Button
            color="danger"
            disabled={isSubmitting}
            onClick={() => runSubmit(publishStatus)}
          >
            {t("catalogProductStudio.wizardRetry")}
          </Button>
        </div>
      ) : null}

      <div className="catalog-wizard">
        <div className="catalog-wizard-main">
          {step < 4 ? (
            <section className="catalog-wizard-panel">
              {step === 1 ? (
                <Step1Product
                  t={t}
                  core={core}
                  errors={fieldErrors}
                  codeConflict={codeConflict}
                  onFieldChange={handleCoreFieldChange}
                  onEnrichmentChange={handleEnrichmentChange}
                  onAddDetailSection={handleAddDetailSection}
                  onRemoveDetailSection={handleRemoveDetailSection}
                  onDetailSectionChange={handleDetailSectionChange}
                  brands={brands}
                  merchandisingTypes={merchandisingTypes}
                  productFamilyOptions={productFamilyOptions}
                  untitledLabel={untitledLabel}
                />
              ) : null}

              {step === 2 ? (
                <Step2Variants
                  t={t}
                  colors={colors}
                  sizes={sizes}
                  onToggleColor={toggleColor}
                  onRemoveColor={removeColor}
                  onAddCustomColor={addCustomColor}
                  onToggleSize={toggleSize}
                  onAddCustomSize={addCustomSize}
                  onApplySizeRange={applySizeRange}
                  apparelSizeSystem={apparelSizeSystem}
                  onApparelSizeSystemChange={changeApparelSizeSystem}
                  cmsColorOptions={cmsColorOptions}
                  colorSearchTerm={colorSearchTerm}
                  onColorSearchChange={setColorSearchTerm}
                  recommendedSizes={recommendedSizes}
                  optionGuidance={optionGuidance}
                  variantCount={variantCount}
                  getSwatch={getSwatch}
                />
              ) : null}

              {step === 3 ? (
                <Step3PhotosStock
                  t={t}
                  colorGroups={colorGroups}
                  mediaItems={mediaItems}
                  quantityByRowKey={quantityByRowKey}
                  onUploadForColor={handleUploadForColor}
                  onRemoveMediaItem={removeMediaItem}
                  onSetProductCover={setProductCover}
                  onSetColorCover={setColorCover}
                  onMoveMediaItem={moveMediaItem}
                  onQuantityChange={handleQuantityChange}
                  onSameStockForColor={sameStockForColor}
                  onClearStockForColor={clearStockForColor}
                  onSetPrimaryColor={setPrimaryColor}
                  onRemoveColorGroup={removeColor}
                  isUploadingMediaAsset={isUploadingMediaAsset}
                  locations={locations}
                  locationId={locationId}
                  onLocationChange={setLocationId}
                  variantCount={variantCount}
                  matrixText={matrixText}
                  getSwatch={getSwatch}
                />
              ) : null}

              <div className="catalog-wizard-footer">
                {step === 1 ? (
                  <Button
                    color="secondary"
                    className="btn-lg-touch"
                    onClick={() => navigate("/admin/catalog/products")}
                  >
                    {t("catalogProductStudio.wizardCancel")}
                  </Button>
                ) : (
                  <Button color="secondary" className="btn-lg-touch" onClick={() => setStep(step - 1)}>
                    <WizardIcon name="arrowLeft" size={16} />
                    {t("catalogProductStudio.wizardBack")}
                  </Button>
                )}
                <Button
                  color="primary"
                  className="btn-lg-touch"
                  disabled={nextDisabled}
                  onClick={() => goToStep(step + 1)}
                >
                  {t("catalogProductStudio.wizardNext")}
                  <WizardIcon name="arrowRight" size={16} />
                </Button>
              </div>
            </section>
          ) : (
            <Step4Review
              t={t}
              onEdit={(targetStep) => setStep(targetStep)}
              getSwatch={getSwatch}
              checklist={checklist}
              summary={{
                title: core.title,
                description: core.description,
                brandName,
                categoryLabel,
                typeLabel,
                price: core.price,
                compareAtPrice: core.compareAtPrice,
                cost: core.cost,
                colors,
                variantCount,
                photos: orderedPhotoUrls,
                metaCatalogEnabled: core.metaCatalogEnabled,
                webshopFilledCount,
                coverImageUrl,
                colorSummaries,
              }}
            />
          )}
        </div>

        <CreatePreviewPanel
          t={t}
          step={step}
          title={core.title}
          price={core.price}
          compareAtPrice={core.compareAtPrice}
          description={core.description}
          brandName={brandName}
          categoryLabel={categoryLabel}
          colors={colors}
          sizes={sizes}
          images={orderedPhotoUrls}
          stockByColor={stockByColor}
          totalStock={totalStock}
          isReadyToPublish={checklist.hard.length === 0}
          getSwatch={getSwatch}
        >
          {step === 4 ? (
            <div className="catalog-wizard-preview__card catalog-wizard-good-to-know">
              <div className="catalog-wizard-info catalog-wizard-info--plain">
                <WizardIcon name="info" size={20} className="catalog-wizard-info__svg" />
                <div>
                  <strong>{t("catalogProductStudio.step4GoodToKnowTitle")}</strong>
                  <ul>
                    <li>{t("catalogProductStudio.step4GoodToKnowDraft")}</li>
                    <li>{t("catalogProductStudio.step4GoodToKnowNoPhotos")}</li>
                    <li>{t("catalogProductStudio.step4GoodToKnowStudio")}</li>
                  </ul>
                </div>
              </div>
            </div>
          ) : null}
        </CreatePreviewPanel>
      </div>

      {step === 4 ? (
        <div className="catalog-wizard-footer catalog-wizard-footer--split catalog-wizard-footer-bar">
          <Button color="secondary" className="btn-lg-touch" disabled={isSubmitting} onClick={() => setStep(3)}>
            <WizardIcon name="arrowLeft" size={16} />
            {t("catalogProductStudio.wizardBack")}
          </Button>
          <label className="catalog-wizard-create-another">
            <input
              type="checkbox"
              checked={createAnother}
              disabled={isSubmitting}
              onChange={(event) => changeCreateAnother(event.target.checked)}
            />
            {t("catalogProductStudio.createAnother")}
          </label>
          <div className="catalog-wizard-footer__group">
            <Button
              color="secondary"
              className="btn-lg-touch"
              disabled={submitDisabled}
              onClick={() => {
                setPublishStatus("draft");
                runSubmit("draft");
              }}
            >
              {isSubmitting && publishStatus === "draft"
                ? t("catalogProductStudio.wizardSubmitting")
                : t("catalogProductStudio.wizardSaveDraft")}
            </Button>
            <Button
              color="secondary"
              className="btn-lg-touch"
              disabled={submitDisabled}
              onClick={() => {
                setPublishStatus("active");
                runSubmit("active");
              }}
            >
              {isSubmitting && publishStatus === "active"
                ? t("catalogProductStudio.wizardSubmitting")
                : t("catalogProductStudio.wizardCreatePublish")}
            </Button>
            <Button
              color="primary"
              className="btn-lg-touch"
              disabled={submitDisabled}
              onClick={() => {
                setPublishStatus("unpublished");
                runSubmit("unpublished");
              }}
            >
              {isSubmitting && publishStatus === "unpublished"
                ? t("catalogProductStudio.wizardSubmitting")
                : t("catalogProductStudio.wizardCreatePosOnly")}
              <WizardIcon name="arrowRight" size={16} />
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default CatalogProductCreateWizard;
